import PropTypes from 'prop-types';
import { path, propEq, find } from 'ramda';
import { connect } from 'react-redux';
import { createSelector } from 'reselect';
import React, { Component } from 'react';

import Avatar from '@material-ui/core/Avatar';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';

import grey from '@material-ui/core/es/colors/grey';
import { Business, Person } from '@material-ui/icons';

import { readEndpointDedup } from '../../modules/reducers/api';

const styles = {
  message: {
    name: {
      color: 'rgba(0, 0, 0, 0.87)', // darkBlack from material-ui
    },
  },
  datetime: {
    fontSize: 12,
    color: grey[700],
  },
};

const etaMap = {
  'less than 5': '<5 m',
  'less than 10': '5-10 m',
  'less than 15': '10-15 m',
  'less than 30': '15-30 m',
  'more than 30': '>30 m',
};

class InvitedDriverMessage extends Component {
  static propTypes = {
    // own props
    message: PropTypes.shape({
      type: PropTypes.oneOf(['dashboard-message']).isRequired,
      id: PropTypes.string.isRequired,
      attributes: PropTypes.shape({
        'created-at': PropTypes.string.isRequired,
        'action': PropTypes.oneOf(['CREATE', 'UPDATE', 'DELETE']).isRequired,
        'sender-url': PropTypes.string.isRequired,
        'sender-type-name': PropTypes.oneOf(['invited-driver']).isRequired,
        'sender-id': PropTypes.number.isRequired,
        'payload': PropTypes.shape({}),
      }).isRequired,
    }).isRequired,
    routeBlockageNames: PropTypes.shape({}).isRequired,

    // state props
    company: PropTypes.shape({}),
    companyId: PropTypes.string.isRequired,
    driver: PropTypes.shape({}),
    driverId: PropTypes.string,
    routeBlockageId: PropTypes.string.isRequired,

    // dispatch props
    readEndpointDedup: PropTypes.func.isRequired,
  };

  componentDidMount() {
    const { message, companyId, company, driverId, driver, readEndpointDedup } = this.props;

    if (!company) readEndpointDedup(`company/${companyId}`);
    if (!message.attributes.merged && !driver) readEndpointDedup(`api-user/${driverId}`);
  };

  shouldComponentUpdate(nextProps, nextState) {
    // Only update if a previously unavailable company and (optional) driver
    // have loaded.
    if (!this.props.company && nextProps.company) return true;
    if (this.props.driverId && !this.props.driver && nextProps.driver) return true;

    return false;
  };

  render() {
    const {
      message, routeBlockageNames,
      routeBlockageId, companyId, company, driverId, driver,
    } = this.props;

    if (!company || (driverId && !driver)) return (<ListItem insetChildren={true} primaryText="Laden..." />);

    const routeBlockageName = routeBlockageNames[routeBlockageId] || 'Onbekend';
    const companyName = path(['attributes', 'name'], company) || 'Onbekend';
    const datetime = new Date(path(['attributes', 'created-at'], message));

    switch (message.attributes.action) {
      case 'CREATE': {
        let title = '1 chauffeur uitgenodigd';

        if (message.attributes.merged) {
          title = message.attributes['sender-ids'].length + ' chauffeurs uitgenodigd';
        }

        return (
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar>
                <Business />
              </Avatar>
            </ListItemAvatar>

            <ListItemText
              primary={
                <span>
                  {title}<br />
                  <span style={styles.datetime}>{datetime.toLocaleString()}</span>
                </span>
              }
              secondary={
                <span style={{ margin: 0 }}>
                  <span style={styles.message.name}>{companyName}</span> --
                  Op stremming `{routeBlockageName}`.
                </span>
              }
            />
          </ListItem>
        );
      }
      case 'UPDATE': {
        const name = (path(['attributes', 'first-name'], driver) || '') + ' ' +
          (path(['attributes', 'last-name'], driver) || '');
        let title = 'Uitnodiging gewijzigd';
        let sub_message = 'Op stremming `' + routeBlockageName + '`.';

        if (message.attributes.payload.accepted === true) {
          title = 'Uitnodiging geaccepteerd';
          sub_message = 'Stalling in ' +
            etaMap[message.attributes.payload.eta] +
            ' `' + routeBlockageName + '`';
        } else if (message.attributes.payload.accepted === false) {
          title = 'Uitnodiging geweigerd';
        }

        return (
          <ListItem alignItems="flex-start">
            <ListItemAvatar>
              <Avatar>
                <Person />
              </Avatar>
            </ListItemAvatar>

            <ListItemText
              primary={
                <span>
                  {title}<br />
                  <span style={styles.datetime}>{datetime.toLocaleString()}</span>
                </span>
              }
              secondary={
                <span style={{ margin: 0 }}>
                  <span style={styles.message.name}>{name} ({companyName})</span> --
                  {sub_message}
                </span>
              }
            />
          </ListItem>
        );
      }
      default: {
        return null;
      }
    }
  };
}

const selectRouteBlockageId = (_, ownProps) => (String(path(['message', 'attributes', 'payload', 'route-blockage'], ownProps)));
const selectCompanyId = (_, ownProps) => (String(path(['message', 'attributes', 'payload', 'company'], ownProps)));
const selectCompanies = state => path(['api', 'company', 'data'], state) || [];
const getCompany = createSelector([selectCompanyId, selectCompanies], (cid, cs) => {
  return find(propEq('id', cid))(cs);
});
const selectDriverId = (_, ownProps) => (String(path(['message', 'attributes', 'payload', 'driver'], ownProps)));
const selectDrivers = state => path(['api', 'api-user', 'data'], state) || [];
const getDriver = createSelector([selectDriverId, selectDrivers], (did, ds) => {
  return find(propEq('id', did))(ds);
});

const mapStateToProps = (state, ownProps) => ({
  company: getCompany(state, ownProps),
  companyId: selectCompanyId(state, ownProps),
  driver: getDriver(state, ownProps),
  driverId: selectDriverId(state, ownProps),
  routeBlockageId: selectRouteBlockageId(state, ownProps),
});

const mapDispatchToProps = {
  readEndpointDedup,
};

export default connect(mapStateToProps, mapDispatchToProps)(InvitedDriverMessage);
